/**
 * 离线程序化"山地"底图：地形与影像均不依赖任何网络服务 / Token。
 *
 *  - MountainTerrainProvider: 以 HeightmapTerrainData 输出的程序化地形，
 *    由若干高斯山峰 + 分形噪声起伏构成；
 *  - HillshadeImageryProvider: 基于同一高度函数实时生成的山体阴影影像
 *    （高程配色 + 西北向光照），让地形在视觉上真正"立体"。
 *
 * 山峰集中布置在区域风场模拟域（118.5°E, 28°N，约 8km x 8km）内，
 * 形成"几个山头、几公里尺度"的立体场景。
 */

/* global Cesium */

const DEG2RAD = Math.PI / 180;

// 山体中心与区域风场模拟域中心一致
const CENTER_LON = 118.5;
const CENTER_LAT = 28.0;
// 米/度 近似（中心纬度处）
const M_PER_DEG_LAT = 110900;
const M_PER_DEG_LON = 111320 * Math.cos(CENTER_LAT * DEG2RAD);

// 山峰定义：经度(度)、纬度(度)、峰高(米)、半径(米，高斯标准差)
const PEAK_DEFS = [
  { lon: 118.480, lat: 27.986, h: 1280, r: 1700 },
  { lon: 118.513, lat: 28.009, h: 1120, r: 1500 },
  { lon: 118.496, lat: 28.023, h: 940, r: 1350 },
  { lon: 118.529, lat: 27.980, h: 860, r: 1600 },
  { lon: 118.463, lat: 28.013, h: 760, r: 1250 },
  { lon: 118.510, lat: 27.968, h: 640, r: 1150 },
  { lon: 118.483, lat: 28.038, h: 560, r: 1300 }
];

// 预换算为相对中心的米坐标，避免逐像素重复换算
const PEAK_X = PEAK_DEFS.map(p => (p.lon - CENTER_LON) * M_PER_DEG_LON);
const PEAK_Y = PEAK_DEFS.map(p => (p.lat - CENTER_LAT) * M_PER_DEG_LAT);
const PEAK_H = PEAK_DEFS.map(p => p.h);
const PEAK_R2 = PEAK_DEFS.map(p => 2 * p.r * p.r);

/* ---------------- 确定性噪声 ---------------- */

function hash2(ix, iy) {
  let n = (Math.imul(ix, 374761393) + Math.imul(iy, 668265263)) | 0;
  n = Math.imul(n ^ (n >>> 13), 1274126177);
  n ^= n >>> 16;
  return (n >>> 0) / 4294967296;
}

function valueNoise(x, y) {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  const fx = x - ix;
  const fy = y - iy;
  const ux = fx * fx * (3 - 2 * fx);
  const uy = fy * fy * (3 - 2 * fy);
  const a = hash2(ix, iy);
  const b = hash2(ix + 1, iy);
  const c = hash2(ix, iy + 1);
  const d = hash2(ix + 1, iy + 1);
  return a + (b - a) * ux + (c - a) * uy + (a - b - c + d) * ux * uy;
}

function fbm(x, y, octaves) {
  let v = 0;
  let amp = 0.5;
  let freq = 1;
  for (let i = 0; i < octaves; i++) {
    v += amp * valueNoise(x * freq, y * freq);
    freq *= 2;
    amp *= 0.5;
  }
  return v;
}

/* ---------------- 高度函数 ---------------- */

/**
 * 程序化高程（米）。
 * 结构：7 个高斯山峰（带表面噪声）* 35km 山区包络 + 全球缓起伏。
 */
function terrainHeight(lonDeg, latDeg) {
  const dx = (lonDeg - CENTER_LON) * M_PER_DEG_LON;
  const dy = (latDeg - CENTER_LAT) * M_PER_DEG_LAT;
  const dist2 = dx * dx + dy * dy;

  // 山区包络：半径约 35km 的高斯衰减
  const envelope = Math.exp(-dist2 / (2 * 35000 * 35000));

  // 高斯山峰叠加
  let peaks = 0;
  for (let i = 0; i < PEAK_DEFS.length; i++) {
    const px = dx - PEAK_X[i];
    const py = dy - PEAK_Y[i];
    peaks += PEAK_H[i] * Math.exp(-(px * px + py * py) / PEAK_R2[i]);
  }

  // 山体表面细节（~600m 波长）与丘陵起伏（~2.6km 波长）
  const detail = fbm(dx / 620 + 13.7, dy / 620 - 7.1, 4);
  const rolling = fbm(dx / 2600 - 31.4, dy / 2600 + 19.2, 3);

  let h = (peaks * (0.72 + 0.55 * detail) + (rolling - 0.5) * 420) * envelope;
  // 山区之外的全球缓起伏，避免大地完全平坦
  h += (fbm(dx / 9000 + 55.5, dy / 9000 - 21.8, 2) - 0.5) * 140;
  return h > 0 ? h : 0;
}

/* ================================================================== */
/* 地形提供者                                                          */
/* ================================================================== */

class MountainTerrainProvider {
  constructor(options = {}) {
    this._tilingScheme = new Cesium.GeographicTilingScheme({
      ellipsoid: Cesium.Ellipsoid.WGS84
    });
    this._width = options.tileWidth || 65;
    this._height = options.tileHeight || 65;
    this._maxLevel =
      options.maximumLevel !== undefined ? options.maximumLevel : 16;
    this._levelZeroMaximumGeometricError = Cesium.TerrainProvider.getEstimatedLevelZeroGeometricErrorForAHeightmap(
      this._tilingScheme.ellipsoid,
      this._width,
      this._tilingScheme.getNumberOfXTilesAtLevel(0)
    );
    this._errorEvent = new Cesium.Event();
    this._credit = new Cesium.Credit("程序化山地地形");
    this._readyPromise = Cesium.when.resolve(true);
  }

  get errorEvent() {
    return this._errorEvent;
  }
  get credit() {
    return this._credit;
  }
  get tilingScheme() {
    return this._tilingScheme;
  }
  get ready() {
    return true;
  }
  get readyPromise() {
    return this._readyPromise;
  }
  get hasWaterMask() {
    return false;
  }
  get hasVertexNormals() {
    return false;
  }

  requestTileGeometry(x, y, level) {
    const rect = this._tilingScheme.tileXYToRectangle(x, y, level);
    const west = Cesium.Math.toDegrees(rect.west);
    const south = Cesium.Math.toDegrees(rect.south);
    const east = Cesium.Math.toDegrees(rect.east);
    const north = Cesium.Math.toDegrees(rect.north);

    const w = this._width;
    const h = this._height;
    const buffer = new Float32Array(w * h);
    const lonStep = (east - west) / (w - 1);
    const latStep = (north - south) / (h - 1);

    // 缓冲行序：第 0 行对应北边界（HeightmapTessellator 约定）
    let k = 0;
    for (let r = 0; r < h; r++) {
      const lat = north - r * latStep;
      for (let c = 0; c < w; c++) {
        buffer[k++] = terrainHeight(west + c * lonStep, lat);
      }
    }

    return Cesium.when.resolve(
      new Cesium.HeightmapTerrainData({
        buffer: buffer,
        width: w,
        height: h,
        childTileMask: level < this._maxLevel ? 15 : 0
      })
    );
  }

  getLevelMaximumGeometricError(level) {
    return this._levelZeroMaximumGeometricError / (1 << level);
  }

  getTileDataAvailable(x, y, level) {
    return level <= this._maxLevel;
  }

  loadTileDataAvailability() {
    return Cesium.when.resolve();
  }
}

/* ================================================================== */
/* 山体阴影影像提供者                                                  */
/* ================================================================== */

// 光照方向（指向西北上方的光源），x 朝东、y 朝北、z 朝上
const LIGHT = (function () {
  const v = [-0.6, 0.55, 0.62];
  const len = Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]);
  return [v[0] / len, v[1] / len, v[2] / len];
})();

// 高程配色节点：[高程(米), r, g, b]（谷地草地 → 橄榄 → 棕色 → 灰岩）
const COLOR_STOPS = [
  [0, 0.26, 0.37, 0.22],
  [250, 0.3, 0.38, 0.22],
  [600, 0.36, 0.36, 0.24],
  [1000, 0.45, 0.39, 0.3],
  [1500, 0.6, 0.57, 0.53],
  [2000, 0.72, 0.7, 0.68]
];

function rampColor(h, out) {
  const stops = COLOR_STOPS;
  if (h <= stops[0][0]) {
    out[0] = stops[0][1];
    out[1] = stops[0][2];
    out[2] = stops[0][3];
    return;
  }
  for (let i = 1; i < stops.length; i++) {
    if (h <= stops[i][0]) {
      const t = (h - stops[i - 1][0]) / (stops[i][0] - stops[i - 1][0]);
      out[0] = stops[i - 1][1] + (stops[i][1] - stops[i - 1][1]) * t;
      out[1] = stops[i - 1][2] + (stops[i][2] - stops[i - 1][2]) * t;
      out[2] = stops[i - 1][3] + (stops[i][3] - stops[i - 1][3]) * t;
      return;
    }
  }
  const last = stops[stops.length - 1];
  out[0] = last[1];
  out[1] = last[2];
  out[2] = last[3];
}

class HillshadeImageryProvider {
  constructor(options = {}) {
    this._tilingScheme = new Cesium.GeographicTilingScheme({
      ellipsoid: Cesium.Ellipsoid.WGS84
    });
    this._tileWidth = options.tileWidth || 128;
    this._tileHeight = options.tileHeight || 128;
    this._maximumLevel =
      options.maximumLevel !== undefined ? options.maximumLevel : 14;
    this._errorEvent = new Cesium.Event();
    this._readyPromise = Cesium.when.resolve(true);
  }

  get proxy() {
    return undefined;
  }
  get tileWidth() {
    return this._tileWidth;
  }
  get tileHeight() {
    return this._tileHeight;
  }
  get maximumLevel() {
    return this._maximumLevel;
  }
  get minimumLevel() {
    return 0;
  }
  get tilingScheme() {
    return this._tilingScheme;
  }
  get rectangle() {
    return this._tilingScheme.rectangle;
  }
  get tileDiscardPolicy() {
    return undefined;
  }
  get errorEvent() {
    return this._errorEvent;
  }
  get ready() {
    return true;
  }
  get readyPromise() {
    return this._readyPromise;
  }
  get credit() {
    return undefined;
  }
  get hasAlphaChannel() {
    return false;
  }

  getTileCredits() {
    return undefined;
  }

  requestImage(x, y, level) {
    const w = this._tileWidth;
    const h = this._tileHeight;
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    const img = ctx.createImageData(w, h);
    const data = img.data;

    const rect = this._tilingScheme.tileXYToRectangle(x, y, level);
    const west = Cesium.Math.toDegrees(rect.west);
    const south = Cesium.Math.toDegrees(rect.south);
    const east = Cesium.Math.toDegrees(rect.east);
    const north = Cesium.Math.toDegrees(rect.north);
    const lonStep = (east - west) / (w - 1);
    const latStep = (north - south) / (h - 1);
    // 差分采样间距（米），用于法线估计
    const cellX = lonStep * M_PER_DEG_LON;
    const cellY = latStep * M_PER_DEG_LAT;

    const col = [0, 0, 0];
    let k = 0;
    for (let r = 0; r < h; r++) {
      const lat = north - r * latStep;
      for (let c = 0; c < w; c++) {
        const lon = west + c * lonStep;

        // 中心差分求坡度
        const gx =
          terrainHeight(lon + lonStep, lat) -
          terrainHeight(lon - lonStep, lat);
        const gy =
          terrainHeight(lon, lat + latStep) -
          terrainHeight(lon, lat - latStep);
        let nx = -gx / (2 * cellX);
        let ny = -gy / (2 * cellY);
        const inv = 1 / Math.sqrt(nx * nx + ny * ny + 1);
        nx *= inv;
        ny *= inv;
        const nz = inv;

        // 西北向光照 + 环境光
        const lambert = Math.max(
          nx * LIGHT[0] + ny * LIGHT[1] + nz * LIGHT[2],
          0
        );
        const shade = 0.32 + 0.78 * lambert;

        const hc = terrainHeight(lon, lat);
        rampColor(hc, col);

        // 陡坡裸露岩石
        const steep = Math.min(Math.max((1 - nz) * 2.2, 0), 1) * 0.7;
        let cr = col[0] + (0.42 - col[0]) * steep;
        let cg = col[1] + (0.38 - col[1]) * steep;
        let cb = col[2] + (0.34 - col[2]) * steep;

        // 细微颗粒，打破色带
        const grain =
          0.92 +
          0.16 *
            valueNoise(
              (lon - CENTER_LON) * (M_PER_DEG_LON / 450),
              (lat - CENTER_LAT) * (M_PER_DEG_LAT / 450)
            );

        data[k] = Math.min(255, cr * shade * grain * 255) | 0;
        data[k + 1] = Math.min(255, cg * shade * grain * 255) | 0;
        data[k + 2] = Math.min(255, cb * shade * grain * 255) | 0;
        data[k + 3] = 255;
        k += 4;
      }
    }

    ctx.putImageData(img, 0, 0);
    return Cesium.when.resolve(canvas);
  }
}

export { MountainTerrainProvider, HillshadeImageryProvider, terrainHeight };
